/**
 * CSS background helpers for parchment / slate textures.
 * Bitmap tiles live in `public/textures/` plus SVG noise.
 */

/** Sidebar palette (not all MUI versions retain arbitrary `palette` keys on the theme object). */
export const VINTAGE_SIDEBAR = {
  // main: '#8F8D7A',
  main: '#797A66',
  deep: '#1e2822',
  contrastText: '#f2ebe0',
};

/** Primary-light (#4a7c62) wash for the active sidebar nav row. */
const SIDEBAR_NAV_SELECTED_BACKGROUND = 'rgba(74, 124, 98, 0.58)';
const SIDEBAR_NAV_SELECTED_BACKGROUND_HOVER = 'rgba(74, 124, 98, 0.68)';
const SIDEBAR_NAV_SELECTED_TEXT = '#faf8f3';

/**
 * Drawer content `sx` for moss sidebar nav lists (Engage + Brewhouse).
 * Selected rows use a lighter emerald panel per Parchment Ledger rails.
 */
export function sidebarDrawerContentSx() {
  return {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: VINTAGE_SIDEBAR.contrastText,
    '& .MuiListItemIcon-root': {
      color: 'rgba(242, 235, 224, 0.9)',
    },
    '& .MuiListItemButton-root': {
      borderRadius: 1,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      '&.Mui-selected': {
        backgroundColor: SIDEBAR_NAV_SELECTED_BACKGROUND,
        '&:hover': {
          backgroundColor: SIDEBAR_NAV_SELECTED_BACKGROUND_HOVER,
        },
      },
      '&.Mui-selected .MuiListItemIcon-root': {
        color: SIDEBAR_NAV_SELECTED_TEXT,
      },
    },
    '& .MuiListItemButton-root.Mui-selected .MuiListItemText-primary': {
      color: SIDEBAR_NAV_SELECTED_TEXT,
      fontWeight: 600,
    },
    '& .MuiListItemText-primary': {
      color: 'rgba(242, 235, 224, 0.95)',
    },
  };
}

function encodeSvgNoise(opacity) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='128' height='128'><filter id='n' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(#n)' opacity='${opacity}'/></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

export const noiseSoft = encodeSvgNoise(0.11);
export const noiseSidebar = encodeSvgNoise(0.14);

/** Full parchment: wash + tiled bitmap + SVG grain — use on page background only */
export function parchmentStack(baseColor) {
  return {
    backgroundColor: baseColor,
    backgroundImage: `
      linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, transparent 45%),
      url('/textures/parchment.webp'),
      ${noiseSoft}
    `,
    backgroundSize: 'auto, 800px 800px, auto',
    backgroundBlendMode: 'normal, multiply, soft-light',
  };
}

/** Flat paper fill for cards, dialogs, app bar — no bitmap/SVG grain (avoids harsh noise on mobile WebKit). */
export function elevatedPaperSurface(baseColor) {
  return {
    backgroundColor: baseColor,
  };
}

/**
 * Dark olive sidebar: slate tile + grain + depth wash.
 * The top `background-image` layer is painted above the rest — it must stay translucent
 * or it would hide the texture (unlike an opaque sidebarMain → sidebarDeep gradient).
 */
export function sidebarStack(sidebarMain) {
  return {
    backgroundColor: sidebarMain,
    backgroundImage: `
      linear-gradient(
        175deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(0, 0, 0, 0.2) 40%,
        rgba(0, 0, 0, 0.4) 100%
      ),
      url('/textures/sidebar-slate.webp'),
      ${noiseSidebar}
    `,
    backgroundSize: 'auto, 300px 300px, auto',
    backgroundBlendMode: 'normal, soft-light, soft-light',
  };
}
